import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PreviewHeader = ({ 
  plannerData, 
  onSave, 
  onShare,
  isSaving = false,
  className = ""
}) => {
  const navigate = useNavigate();

  const handleBackToWizard = () => {
    navigate('/planner-setup-wizard');
  };

  const handleGoToDashboard = () => {
    navigate('/user-account-dashboard');
  };

  const plannerStats = {
    totalPages: 365,
    sections: ['Monthly', 'Weekly', 'Daily', 'Notes'],
    lastModified: new Date()?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  };

  return (
    <div className={`bg-card border border-border rounded-lg p-6 ${className}`}>
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
        {/* Left Section - Planner Info */}
        <div className="flex-1">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
              <Icon name="FileText" size={24} color="white" />
            </div>
            
            <div className="flex-1 min-w-0">
              <h1 className="font-heading text-2xl font-bold text-foreground mb-2">
                {plannerData?.coverTitle || "My Planner"}
              </h1>
              
              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center space-x-1">
                  <Icon name="Calendar" size={14} />
                  <span className="font-body">
                    {plannerData?.startDate || "2024-01-01"} - {plannerData?.endDate || "2024-12-31"}
                  </span>
                </div>
                
                <div className="flex items-center space-x-1">
                  <Icon name="FileText" size={14} />
                  <span className="font-body">{plannerStats?.totalPages} pages</span>
                </div>
                
                <div className="flex items-center space-x-1">
                  <Icon name="Clock" size={14} />
                  <span className="font-body">Modified {plannerStats?.lastModified}</span>
                </div>
              </div>

              {/* Sections Tags */}
              <div className="flex flex-wrap gap-2 mt-3">
                {plannerStats?.sections?.map((section) => (
                  <span
                    key={section}
                    className="px-2 py-1 bg-secondary/10 text-secondary text-xs font-caption rounded-full"
                  >
                    {section}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Right Section - Actions */}
        <div className="flex flex-col sm:flex-row gap-3 lg:flex-shrink-0">
          <Button
            variant="ghost"
            onClick={handleBackToWizard}
            iconName="ArrowLeft"
            iconPosition="left"
            size="sm"
          >
            Back to Setup
          </Button>

          <Button
            variant="outline"
            onClick={onShare}
            iconName="Share2"
            iconPosition="left"
            size="sm"
          >
            Share Preview
          </Button>

          <Button
            variant="secondary"
            onClick={onSave}
            loading={isSaving}
            iconName="Save"
            iconPosition="left"
            size="sm"
          >
            {isSaving ? 'Saving...' : 'Save Draft'}
          </Button>

          <Button
            variant="outline"
            onClick={handleGoToDashboard}
            iconName="LayoutDashboard"
            iconPosition="left"
            size="sm"
          >
            Dashboard
          </Button>
        </div>
      </div>
      {/* Progress Indicator */}
      <div className="mt-6 pt-4 border-t border-border">
        <div className="flex items-center justify-between mb-2">
          <span className="font-body text-sm text-muted-foreground">
            Setup Progress
          </span>
          <span className="font-body text-sm font-medium text-success">
            Complete
          </span>
        </div>
        
        <div className="w-full bg-muted rounded-full h-2">
          <div className="bg-success h-2 rounded-full w-full transition-all duration-500" />
        </div>
        
        <div className="flex justify-between mt-2 text-xs text-muted-foreground font-caption">
          <span>Setup</span>
          <span>Preview</span>
          <span>Generate</span>
        </div>
      </div>
      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        <div className="text-center">
          <div className="font-heading text-lg font-bold text-primary">
            {plannerData?.layout || "Standard"}
          </div>
          <div className="font-caption text-xs text-muted-foreground">Layout</div>
        </div>
        
        <div className="text-center">
          <div className="font-heading text-lg font-bold text-secondary">
            {plannerData?.colorScheme || "Blue"}
          </div>
          <div className="font-caption text-xs text-muted-foreground">Theme</div>
        </div>
        
        <div className="text-center">
          <div className="font-heading text-lg font-bold text-accent">
            {plannerData?.timeFormat === '24' ? '24h' : '12h'}
          </div>
          <div className="font-caption text-xs text-muted-foreground">Time Format</div>
        </div>
        
        <div className="text-center">
          <div className="font-heading text-lg font-bold text-warning">
            {plannerData?.includeWeekends ? 'Yes' : 'No'}
          </div>
          <div className="font-caption text-xs text-muted-foreground">Weekends</div>
        </div>
      </div>
    </div>
  );
};

export default PreviewHeader;